'use client'

import { type FC } from 'react'

import {
  CrossSellHorizontalSlider,
  CrossSellMetadataProvider,
  CrossSellWidgetContainer,
  CrossSellWidgetProvider,
  CrossSellWidgetTitle,
  useCrossSellIntersection,
  useCrossSellWidgetTitle,
  useSwrCrossSellHorizontalSlider,
} from '../../../cross-sell'
import { gtmDataLayerPushCswProduct } from '../../../gtm'
import { productAccessorAvailableProductWidgetType } from '../../model/productAccessor/productAccessorAvailableProductWidgetType'
import { productAccessorStockStatusIsAvailable } from '../../model/productAccessor/productAccessorStockStatusIsAvailable'
import { PRODUCT_TEMPLATES } from '../../model/productTemplates'
import { useProductCrossSellFirstImpressionWidget } from '../ProductCrossSell/ProductCrossSellFirstImpressionSliderWidget/useProductCrossSellFirstImpressionWidget'
import { useProductPageContext } from '../productPageContext'

import { useProductAvailableCrossSellSliderRequestParams } from './useProductAvailableCrossSellSliderRequestParams'
import { useProductAvailableCrossSellWidget } from './useProductAvailableCrossSellWidget'

export const ProductAvailableCrossSellSliderWidget: FC = () => {
  const { currentVariant, productTemplate } = useProductPageContext()
  const available = useProductAvailableCrossSellWidget()
  const requestParams = useProductAvailableCrossSellSliderRequestParams()
  const isAvailable = productAccessorStockStatusIsAvailable(currentVariant)
  const widgetType = productAccessorAvailableProductWidgetType({
    isAvailable,
    isRx: productTemplate === PRODUCT_TEMPLATES.RX,
  })
  const firstImpressionCrossSellWidget =
    useProductCrossSellFirstImpressionWidget()

  const { handleIntersection, intersected } = useCrossSellIntersection()

  const { data, error } = useSwrCrossSellHorizontalSlider(
    intersected ? requestParams : undefined,
  )
  const widgetTitle = useCrossSellWidgetTitle({ data, widgetType })

  if (
    !requestParams ||
    /*
     * Similar products will be displayed on top when there are no variants available in stock
     * https://jira.shop-apotheke.com/browse/WSHOME-734
     */
    !available ||
    // https://jira.shop-apotheke.com/browse/CRO-576
    firstImpressionCrossSellWidget ||
    data?.items.length === 0 ||
    error
  ) {
    return null
  }
  const { widgetId } = requestParams

  return (
    <CrossSellMetadataProvider metadata={data?.metadata}>
      <CrossSellWidgetContainer
        onIntersect={handleIntersection}
        widgetData={data}
        widgetId={widgetId}
        widgetType={widgetType}
      >
        <CrossSellWidgetTitle className="mb-3.5 text-xl font-medium tablet:text-2xl">
          {widgetTitle}
        </CrossSellWidgetTitle>
        <CrossSellWidgetProvider value={{ widgetId }}>
          <CrossSellHorizontalSlider
            onGtmDataLayerPush={(cswProductId) => {
              gtmDataLayerPushCswProduct({
                cswProductId,
                productId: currentVariant.upid,
                widgetId,
                widgetTitle,
              })
            }}
            products={data?.items}
            productsPerPage={3}
          />
        </CrossSellWidgetProvider>
      </CrossSellWidgetContainer>
    </CrossSellMetadataProvider>
  )
}
